import { inject, Injectable, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../../environments/environment';
import { AppService } from './app.service';
import { UserService } from './user.service';

type Attempt = {
  id: string;
  user: NonNullable<ReturnType<AppService['getAuthUser']>>;
  popup: Window | null;
  origin: string;
  deadline: number;
  state: 'waiting-ready' | 'obtaining-token' | 'token-sent' | 'terminal';
  interval?: ReturnType<typeof setInterval>;
  timeout?: ReturnType<typeof setTimeout>;
};

const PROTOCOL = 'voxr-pulse-login';
const UNCONFIRMED = 'Could not confirm Pulse sign-in. Check the Pulse tab or try again.';
const FAILED = 'Could not sign in to Pulse. Return to VOXR and try again.';
const RESULT_CODES = new Set([
  'INVALID_MESSAGE', 'AUTHENTICATION_FAILED', 'DEPENDENCY_UNAVAILABLE',
  'SESSION_FAILED', 'CANCELLED', 'TIMEOUT',
]);

@Injectable({ providedIn: 'root' })
export class PulseLoginService implements OnDestroy {
  private app = inject(AppService);
  private userService = inject(UserService);
  private attempt?: Attempt;

  constructor() {
    this.app.authState$.pipe(takeUntilDestroyed()).subscribe(user => {
      const attempt = this.attempt;
      if (attempt && user !== attempt.user) this.finish(attempt, true, FAILED);
      else if (attempt) this.check(attempt);
    });
    this.app.idToken$.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.attempt) this.check(this.attempt);
    });
    this.userService.user$.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.attempt) this.check(this.attempt);
    });
  }

  isAvailable(): boolean {
    return !!environment.app.pulseOrigin && window.location.origin === environment.app.mainOrigin;
  }

  open(): void {
    if (this.attempt && this.check(this.attempt)) {
      try { this.attempt?.popup?.focus(); } catch { /* The deadline still bounds an unreachable tab. */ }
      return;
    }
    if (!this.isAvailable()) return;
    if (this.userService.impersonated()) {
      this.notify('Exit impersonation to open Pulse.');
      return;
    }
    const user = this.app.getAuthUser();
    if (!user) {
      this.notify('Sign in to VOXR to open Pulse.');
      return;
    }
    const attempt: Attempt = {
      id: crypto.randomUUID(), user, popup: null, origin: environment.app.pulseOrigin!,
      deadline: Date.now() + 30_000, state: 'waiting-ready',
    };
    this.attempt = attempt;
    window.addEventListener('message', this.onMessage);
    window.addEventListener('pagehide', this.onPageHide);
    try {
      attempt.popup = window.open(attempt.origin + '/auth/voxr', '_blank');
    } catch {
      this.finish(attempt, false, FAILED);
      return;
    }
    if (!attempt.popup) {
      this.finish(attempt, false, 'Allow pop-ups to open Pulse, then try again.');
      return;
    }
    attempt.interval = setInterval(() => {
      if (this.check(attempt) && attempt.state === 'waiting-ready') this.send(attempt, 'initialize');
    }, 500);
    attempt.timeout = setTimeout(() => this.check(attempt), Math.max(0, attempt.deadline - Date.now()));
    if (this.check(attempt)) this.send(attempt, 'initialize');
  }

  private readonly onMessage = (event: MessageEvent): void => {
    const attempt = this.attempt;
    if (!attempt || event.origin !== attempt.origin || event.source !== attempt.popup) return;
    const data = event.data;
    if (!data || typeof data !== 'object' || Array.isArray(data)
      || data.protocol !== PROTOCOL || data.version !== 1 || data.attemptId !== attempt.id) return;
    if (!this.check(attempt)) return;
    const keys = ['protocol', 'version', 'attemptId', 'type'];
    if (data.type === 'ready') {
      if (Object.keys(data).length !== keys.length || !keys.every(key => Object.hasOwn(data, key))) {
        this.finish(attempt, true, FAILED);
      } else if (attempt.state === 'waiting-ready') {
        attempt.state = 'obtaining-token';
        void this.sendToken(attempt);
      }
    } else if (data.type === 'result') {
      const success = data.status === 'success';
      const valid = success || (data.status === 'error' && RESULT_CODES.has(data.code));
      const expected = [...keys, 'status', ...(success ? [] : ['code'])];
      if (!valid || Object.keys(data).length !== expected.length || !expected.every(key => Object.hasOwn(data, key))) {
        this.finish(attempt, true, FAILED);
      } else if (!success) {
        this.finish(attempt, false, data.code === 'TIMEOUT' ? UNCONFIRMED : FAILED);
      } else if (attempt.state === 'token-sent') {
        this.finish(attempt, false);
      }
    } else if (['initialize', 'token', 'cancel'].includes(data.type)) {
      this.finish(attempt, true, FAILED);
    }
  };

  private readonly onPageHide = (): void => {
    if (this.attempt) this.finish(this.attempt, true);
  };

  private async sendToken(attempt: Attempt): Promise<void> {
    try {
      const idToken = await attempt.user.getIdToken();
      if (!this.check(attempt)) return;
      if (!idToken || idToken.length > 16_384 || /[^\x00-\x7f]/.test(idToken)) {
        this.finish(attempt, true, FAILED);
        return;
      }
      attempt.state = 'token-sent';
      this.send(attempt, 'token', idToken);
    } catch {
      if (this.attempt === attempt) this.finish(attempt, true, FAILED);
    }
  }

  private check(attempt: Attempt): boolean {
    if (this.attempt !== attempt || attempt.state === 'terminal') return false;
    if (Date.now() >= attempt.deadline || attempt.popup?.closed) {
      this.finish(attempt, true, attempt.state === 'token-sent' ? UNCONFIRMED : FAILED);
      return false;
    }
    const user = this.app.getAuthUser();
    if (user !== attempt.user || user?.uid !== attempt.user.uid || this.userService.impersonated() || !this.isAvailable()) {
      this.finish(attempt, true, FAILED);
      return false;
    }
    return true;
  }

  private send(attempt: Attempt, type: 'initialize' | 'token' | 'cancel', idToken?: string): void {
    try {
      attempt.popup?.postMessage({
        protocol: PROTOCOL, version: 1, attemptId: attempt.id, type,
        ...(type === 'token' ? { idToken } : {}),
      }, attempt.origin);
    } catch {
      this.finish(attempt, false, attempt.state === 'token-sent' ? UNCONFIRMED : FAILED);
    }
  }

  private finish(attempt: Attempt, cancel: boolean, detail?: string): void {
    if (this.attempt !== attempt) return;
    this.attempt = undefined;
    attempt.state = 'terminal';
    clearInterval(attempt.interval);
    clearTimeout(attempt.timeout);
    window.removeEventListener('message', this.onMessage);
    window.removeEventListener('pagehide', this.onPageHide);
    if (cancel && !attempt.popup?.closed) this.send(attempt, 'cancel');
    attempt.popup = null;
    if (detail) this.notify(detail);
  }

  private notify(detail: string): void {
    this.app.message$.next({ severity: 'warning', summary: 'Open Pulse', detail });
  }

  ngOnDestroy(): void {
    if (this.attempt) this.finish(this.attempt, true);
  }
}
